// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { z } from 'zod';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
} from '../../../stored-json-contract.types';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../../../stored-json-number';
import {
    CloverWebhookPaymentOperation,
    type CloverWebhookAuthenticationInput,
    type CloverWebhookNotificationRequest,
    type CloverWebhookPaymentUpdate,
    type CloverWebhookRequest,
    type CloverWebhookVerificationRequest,
    type CloverWebhookProcessingResult,
} from '../../../types/ipg/clover/clover-webhook.types';

export const CloverWebhookVerificationRequestSchema: z.ZodType<CloverWebhookVerificationRequest> = z
    .object({
        verificationCode: z.string().trim().min(1),
    })
    .strict();

export const CloverWebhookPaymentUpdateSchema: z.ZodType<CloverWebhookPaymentUpdate> = z
    .object({
        objectId: z.string().regex(/^P:[^:]+$/u),
        type: z.enum([CloverWebhookPaymentOperation.CREATE, CloverWebhookPaymentOperation.UPDATE]),
        ts: z.number().int().nonnegative().max(8_640_000_000_000_000),
    })
    .strict();

const CloverWebhookTimestampSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'CloverWebhookPayload.merchants.update.ts',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'CloverWebhookPaymentUpdateSchema.ts',
        minimum: 0,
        maximum: 8_640_000_000_000_000,
    },
});

export const CloverWebhookNotificationRequestSchema: z.ZodType<CloverWebhookNotificationRequest> = z
    .object({
        appId: z.string().trim().min(1),
        merchants: z.record(z.string().min(1), z.array(CloverWebhookPaymentUpdateSchema).min(1)),
    })
    .strict()
    .refine(
        (request: CloverWebhookNotificationRequest): boolean =>
            Object.keys(request.merchants).length > 0,
        { message: 'At least one Clover merchant update array is required' }
    );

export const CloverWebhookRequestSchema: z.ZodType<CloverWebhookRequest> = z.union([
    CloverWebhookVerificationRequestSchema,
    CloverWebhookNotificationRequestSchema,
]);

const StoredCloverWebhookPaymentUpdateSchema: z.ZodType<CloverWebhookPaymentUpdate> = z.preprocess(
    (value: unknown): unknown =>
        normalizeStoredJsonSafeIntegerObjectFields(value, {
            safeIntegerDomain: CloverWebhookTimestampSafeIntegerDomain,
            fields: ['ts'],
        }),
    CloverWebhookPaymentUpdateSchema
);

const StoredCloverWebhookNotificationRequestSchema: z.ZodType<CloverWebhookNotificationRequest> = z
    .object({
        appId: z.string().trim().min(1),
        merchants: z.record(
            z.string().min(1),
            z.array(StoredCloverWebhookPaymentUpdateSchema).min(1)
        ),
    })
    .strict()
    .refine(
        (request: CloverWebhookNotificationRequest): boolean =>
            Object.keys(request.merchants).length > 0,
        { message: 'At least one Clover merchant update array is required' }
    );

const StoredCloverWebhookRequestSchema: z.ZodType<CloverWebhookRequest> = z.union([
    CloverWebhookVerificationRequestSchema,
    StoredCloverWebhookNotificationRequestSchema,
]);

export const CloverWebhookPayloadStorageContract = defineStoredJsonContract({
    contractName: 'CloverWebhookPayload',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredCloverWebhookRequestSchema',
            schema: StoredCloverWebhookRequestSchema,
            guards: [
                {
                    kind: 'sibling_field_equals',
                    siblingField: 'payload_storage_version',
                    siblingValue: 1,
                },
            ],
            normalize: (value: CloverWebhookRequest): CloverWebhookRequest => value,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredCloverWebhookPayloadRowParser: z.ZodType<CloverWebhookRequest> =
    buildStoredJsonParser(CloverWebhookPayloadStorageContract);

export function serializeCloverWebhookPayloadV1(
    payload: CloverWebhookRequest
): CloverWebhookRequest {
    return CloverWebhookRequestSchema.parse(payload);
}

export const CloverWebhookAuthenticationInputSchema: z.ZodType<CloverWebhookAuthenticationInput> = z
    .object({
        cloverAuth: z.string().trim().min(1).optional(),
    })
    .strict();

export const CloverWebhookProcessingResultSchema: z.ZodType<CloverWebhookProcessingResult> = z
    .object({
        attempted: z.number().int().nonnegative(),
        processed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    })
    .strict();
